
import { logger } from "@/lib/logger";
import { AnalysisErrorType } from "../types";
import * as Sentry from "@sentry/react";

/**
 * Handle errors from fetching website content
 * @param error The error that occurred
 * @param url The website URL that was being fetched
 */
export function handleFetchError(error: unknown, url: string): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  logger.error({
    message: "Error fetching website content",
    error: error instanceof Error ? error : new Error(errorMessage),
    context: { 
      url,
      errorType: AnalysisErrorType.FETCH_ERROR 
    }
  });

  Sentry.captureException(error, {
    tags: {
      error_type: AnalysisErrorType.FETCH_ERROR,
      operation: "content_fetch"
    },
    contexts: {
      website: {
        url,
        service: "supabase_edge_function"
      }
    }
  });
}

/**
 * Handle errors from edge function analysis
 * @param error The error that occurred
 * @param url The website URL that was being analyzed
 */
export function handleEdgeFunctionError(error: unknown, url: string): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  logger.error({
    message: "Error in edge function website analysis",
    error: error instanceof Error ? error : new Error(errorMessage),
    context: { 
      url,
      errorType: AnalysisErrorType.EDGE_FUNCTION_ERROR 
    }
  });

  Sentry.captureException(error, {
    tags: {
      error_type: AnalysisErrorType.EDGE_FUNCTION_ERROR,
      analysis_method: "edge_function"
    },
    contexts: {
      analysis: {
        url,
        service: "supabase_edge_function"
      }
    }
  });
}

/**
 * Handle errors from AI analysis
 * @param error The error that occurred
 * @param url The website URL that was being analyzed
 */
export function handleAIAnalysisError(error: unknown, url: string): void {
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  logger.error({
    message: "Error with AI analysis",
    error: error instanceof Error ? error : new Error(errorMessage),
    context: { 
      url,
      errorType: AnalysisErrorType.AI_ANALYSIS_ERROR
    }
  });

  Sentry.captureException(error, {
    tags: {
      error_type: AnalysisErrorType.AI_ANALYSIS_ERROR,
      analysis_method: "client_side_ai"
    },
    contexts: {
      analysis: {
        url,
        content_length: "unknown", // This would typically come from the content parameter
        service: "openai"
      }
    }
  });
}
